import { apiClient } from './api'
import { API_BASE_URL } from './api-base-url'
import type {
  HistoryListResponse,
  HistoryStats,
  VerificationResult,
  ExportOptions,
  ConvertToMemoryPayload,
  ConvertToMemoryResponse,
  HistoryFilter,
} from '../types/history'

// =============================================================================
// HELPERS
// =============================================================================

type ListHistoryParams = HistoryFilter & { limit?: number; offset?: number }

const ARRAY_PARAM_KEYS = new Set([
  'categories',
  'actorTypes',
  'sections',
  'actorIds',
  'artifactTypes',
])

function appendHistoryParam(searchParams: URLSearchParams, key: string, value: unknown): void {
  if (value === undefined || value === null) return

  if (ARRAY_PARAM_KEYS.has(key) && Array.isArray(value)) {
    if (value.length > 0) searchParams.append(key, value.join(','))
    return
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    searchParams.append(key, String(value))
  }
}

function buildHistoryQueryString(params: ListHistoryParams): string {
  const searchParams = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    appendHistoryParam(searchParams, key, value)
  }
  const queryString = searchParams.toString()
  return queryString ? `?${queryString}` : ''
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.append(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

// =============================================================================
// HISTORY API
// =============================================================================

export async function listHistory(
  caseId: string,
  params?: ListHistoryParams
): Promise<HistoryListResponse> {
  const query = params ? buildHistoryQueryString(params) : ''
  return apiClient.get<HistoryListResponse>(`/cases/${caseId}/history${query}`)
}

export async function getHistoryStats(caseId: string): Promise<HistoryStats> {
  return apiClient.get<HistoryStats>(`/cases/${caseId}/history/stats`)
}

export async function verifyHistoryChain(caseId: string): Promise<VerificationResult> {
  return apiClient.post<VerificationResult>(`/cases/${caseId}/history/verify`)
}

export async function exportHistory(caseId: string, options: ExportOptions): Promise<Blob> {
  const token = localStorage.getItem('evidenx_access_token') ?? ''

  const response = await fetch(`${API_BASE_URL}/cases/${caseId}/history/export`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(options),
  })

  if (!response.ok) {
    const text = await response.text().catch(() => response.statusText)
    throw new Error(`Failed to export history: ${text}`)
  }

  const blob = await response.blob()
  const disposition = response.headers.get('content-disposition')
  const filename =
    disposition?.match(/filename="(.+)"/)?.[1] ?? `history-export-${caseId}.${options.format}`

  triggerDownload(blob, filename)
  return blob
}

export async function convertToMemory(
  caseId: string,
  entryId: string,
  input: ConvertToMemoryPayload
): Promise<ConvertToMemoryResponse> {
  return apiClient.post<ConvertToMemoryResponse>(
    `/cases/${caseId}/history/${entryId}/convert-to-memory`,
    input
  )
}
